const image = (src, alt) => ({ src, alt, type: "image" });
const video = (src, alt, poster) => ({ src, alt, poster, type: "video" });

const paragnosisGallery = Array.from({ length: 8 }, (_, index) =>
  image(
    `/works/ai/Paragnosis/ArcSolver0${index + 1}.png`,
    `Paragnosis ARC solver interface, view ${index + 1}`
  )
);

const engine80Gallery = Array.from({ length: 32 }, (_, index) =>
  image(
    `/works/applications/engine80/E80${index + 1}.png`,
    `Engine 80 creative engine interface, view ${index + 1}`
  )
);

const comityGallery = [
  "Interactive moderation demo in moderator view",
  "Comity research project landing page",
  "Trust as Infrastructure working paper",
  "Working paper results and baseline comparison",
  "Simulation charts showing multidimensional trust",
  "Working paper glossary and threat model",
  "Simulation results dashboard",
  "Headline comparison of moderation approaches",
  "Interactive catalogue of eleven stress tests",
  "Human-supervised moderation decision pipeline",
  "Comity moderation architecture and three-channel model",
].map((alt, index) =>
  image(`/works/research/comity/comity${index + 1}.png`, alt)
);

const photographyGallery = [
  ["Music/DirtyThree.jpeg", "The Dirty Three performing live"],
  ["Music/EdKuepper.jpeg", "Ed Kuepper performing live"],
  ["Music/GlassBeams.jpeg", "Glass Beams performing live"],
  ["Music/jpShilo.jpeg", "JP Shilo performing live"],
  ["Music/NickCave.jpeg", "Nick Cave performing live"],
  ["Music/RowlandSHoward.jpeg", "Rowland S. Howard performing live"],
  ["Music/SilverApples.jpeg", "Silver Apples performing live"],
  ["Music/TheBadSeeds.jpeg", "Nick Cave and the Bad Seeds performing live"],
  ["Portraits/Aurukun03.jpeg", "Portrait made in Aurukun, Queensland"],
  ["Portraits/Joel.jpeg", "Portrait of elder Joel Ngallametta, Aurukun, QLD"],
  ["Portraits/Laura01.jpeg", "Portrait of Dr. Tommy George, Laura, QLD"],
  ["Portraits/Laura02.jpeg", "Portrait of Dr. George Musgrave, Laura, QLD"],
  ["Professional/Harbour8.jpeg", "Sydney Harbour, UTS promotional shoot"],
  ["Professional/Harbour10.jpeg", "Sydney Harbour, UTS promotional shoot"],
  ["Professional/Harbour11.jpeg", "Sydney Harbour, UTS promotional shoot"],
  ["Professional/MilesFranklin01.jpg", "Miles Franklin diaries, State Library of NSW"],
  ["Professional/Sydney01.jpeg", "Sydney city study"],
  ["Professional/Sydney04.jpeg", "Sydney city study"],
  ["Travel/Australia01.jpg", "Australian landscape"],
  ["Travel/Laos.jpg", "Laos travel photograph"],
  ["Travel/Laos01.jpeg", "Laos travel photograph"],
  ["Travel/Yuendemu01.jpg", "Yuendumu, Northern Territory"],
  ["Travel/Yuendemu03.jpg", "Yuendumu, Northern Territory"],
].map(([src, alt]) => image(`/works/photography/${src}`, alt));

export const projects = [
  {
    slug: "comity",
    number: "01",
    title: "Comity",
    shortTitle: "Trust as infrastructure",
    meta: "Strategy · Governance · Trust & Safety",
    period: "Ongoing research",
    categories: ["Strategy", "AI", "UX & UI"],
    colour: "paper",
    cover: "/works/research/comity/comity2.png",
    coverAlt: "Comity trust-based automoderation research project",
    intro:
      "Ongoing independent research and systems-design project exploring how online communities can moderate at scale without confusing disagreement with misconduct.",
    body:
      "Comity frames content moderation as a governance, service-design and trust-infrastructure problem. The work combines strategic modelling, a human-supervised moderation architecture, agent-based simulation, adversarial evaluation, an interactive prototype and a research paper.",
    gallery: comityGallery,
  },
  {
    slug: "paragnosis",
    number: "02",
    title: "Paragnosis",
    shortTitle: "Neuro-symbolic AI",
    meta: "Research · AI · Interface",
    period: "Ongoing",
    categories: ["AI", "Applications", "UX & UI"],
    colour: "cobalt",
    cover: "/works/ai/Paragnosis/ArcSolver05.png",
    coverAlt: "Paragnosis ARC solver task workspace",
    intro:
      "A front-end environment for developing and evaluating a neuro-symbolic world model.",
    body:
      "Paragnosis brings model training, evaluation and visual reasoning into one working interface. The project combines machine-learning research, systems architecture and product design.",
    gallery: paragnosisGallery,
  },
  {
    slug: "onset-spectrum",
    number: "03",
    title: "Onset Spectrum",
    shortTitle: "Sound made visible",
    meta: "C++ · OpenFrameworks · Sound",
    period: "Independent project",
    categories: ["Applications", "Sound"],
    colour: "yellow",
    cover: "/works/applications/onset spectrum/OnsetSpectrum1.png",
    coverAlt: "Onset Spectrum audio analyser interface",
    intro:
      "An interactive audio analyser and visualiser built in C++ with OpenFrameworks.",
    body:
      "A long-running creative coding project exploring the relationship between live audio, spectral analysis and responsive moving image.",
    gallery: [1, 2, 3, 4].map((number) =>
      image(
        `/works/applications/onset spectrum/OnsetSpectrum${number}.png`,
        `Onset Spectrum visualisation, view ${number}`
      )
    ),
  },
  {
    slug: "photography",
    number: "04",
    title: "Photography",
    shortTitle: "Photography with pulse",
    meta: "Music · Portraits · Place",
    period: "Selected archive",
    categories: ["Photography"],
    colour: "red",
    cover: "/works/photography/Music/GlassBeams.jpeg",
    coverAlt: "Glass Beams performing live under stage lights",
    intro:
      "Portraits, performances and places photographed across cultural, professional and community contexts.",
    body:
      "The archive moves between live music, portraiture, commissioned work and travel—always looking for atmosphere, gesture and a sense of place.",
    gallery: photographyGallery,
  },
  {
    slug: "garvan",
    number: "05",
    title: "Garvan digital platform",
    shortTitle: "Complexity made useful",
    meta: "Strategy · Delivery · Web",
    period: "2021—23",
    categories: ["Web", "Strategy", "UX & UI"],
    colour: "paper",
    cover: "/works/web/Garvan1.png",
    coverAlt: "Garvan Institute website redesign",
    intro:
      "A major technology-stack and website redevelopment for the Garvan Institute of Medical Research.",
    body:
      "The work brought together CMS, DAM, CRM and EDM platforms, a MACH-based website, GraphQL integrations, workflow automation, UX research and stakeholder-led delivery.",
    gallery: [1, 2, 3, 4, 5, 6].map((number) =>
      image(`/works/web/Garvan${number}.png`, `Garvan website screen ${number}`)
    ),
    externalUrl: "https://www.garvan.org.au/",
  },
  {
    slug: "generative-ai",
    number: "06",
    title: "Generative AI",
    homeTitle: "Video art",
    shortTitle: "Early synthetic landscapes",
    meta: "Python · Moving image · AI",
    period: "2022",
    categories: ["AI", "Video"],
    colour: "cobalt",
    cover: "/works/ai/Generative/IMG_3904.jpeg",
    coverAlt: "Abstract synthetic landscape generated with machine learning",
    intro:
      "Early generative video experiments from before the current wave of image-generation tools.",
    body:
      "Coded in Python, these studies explore unstable landscapes, memory-like texture and the productive edge between representation and abstraction.",
    gallery: [
      video(
        "/works/ai/Generative/Jungle-web.mp4",
        "Generative jungle video experiment"
      ),
      video(
        "/works/ai/Generative/Mountain-web.mp4",
        "Generative mountain video experiment"
      ),
      ...[3894, 3895, 3901, 3904].map((number) =>
        image(
          `/works/ai/Generative/IMG_${number}.jpeg`,
          `Generative AI landscape still ${number}`
        )
      ),
    ],
  },
  {
    slug: "engine-80",
    number: "07",
    title: "Engine 80",
    shortTitle: "A creative engine",
    meta: "Applications · UX & UI",
    period: "In development",
    categories: ["Applications", "UX & UI"],
    colour: "yellow",
    cover: "/works/applications/engine80/E801.png",
    coverAlt: "Engine 80 creative engine interface",
    intro:
      "A work-in-progress creative and game engine for Mac, iPhone and iPad.",
    body:
      "Engine 80 is an evolving environment for composing interactive scenes, media systems and visual behaviours across Apple platforms.",
    gallery: engine80Gallery,
  },
  {
    slug: "brolly",
    number: "08",
    title: "Brolly",
    shortTitle: "Impulse Control",
    meta: "Swift · Audio · Interface",
    period: "Independent project",
    categories: ["UX & UI", "Applications", "Sound"],
    colour: "red",
    cover: "/works/UXUI/brolly/ImpulseControl1.png",
    coverAlt: "Brolly Impulse Control audio synthesis interface",
    intro:
      "Interface design for the Impulse Control audio synthesis application, coded in Swift.",
    body:
      "A tactile, instrument-like interface that makes complex audio controls legible without losing the character of an experimental tool.",
    gallery: [1, 2, 3, 4, 5, 6].map((number) =>
      image(
        `/works/UXUI/brolly/ImpulseControl${number}.png`,
        `Brolly Impulse Control interface, view ${number}`
      )
    ),
  },
  {
    slug: "digital-service-standard",
    number: "09",
    title: "Digital Service Standard",
    shortTitle: "Better public services",
    meta: "Service design · Guidelines",
    period: "2018—19",
    categories: ["Strategy", "UX & UI"],
    colour: "paper",
    cover: "/works/UXUI/DFSI-DigitalServiceStandard/DFSI1-blue.png",
    coverAlt: "NSW Digital Service Standard interface",
    intro:
      "Digital Service Standard best-practice guidelines for the NSW Department of Finance, Services and Innovation.",
    body:
      "A practical framework for making public digital services clearer, more consistent and centred on the people who use them.",
    gallery: [1, 2, 3, 4, 5].map((number) =>
      image(
        `/works/UXUI/DFSI-DigitalServiceStandard/DFSI${number}-blue.png`,
        `Digital Service Standard screen ${number}`
      )
    ),
  },
  {
    slug: "oneteam",
    number: "10",
    title: "OneTeam",
    shortTitle: "Curriculum connections",
    meta: "Prototype · Power Apps",
    period: "2019—20",
    categories: ["Applications", "Strategy"],
    colour: "cobalt",
    cover: "/works/applications/oneTeamthumbnail.png",
    coverAlt: "OneTeam mobile application prototype",
    intro:
      "A cross-platform application prototype for the NSW Department of Education.",
    body:
      "The prototype explored a more direct way for curriculum advisers and teachers to communicate, supported by workflow automation.",
    gallery: ["0390", "0391", "0393", "0394", "0457", "0458"].map((number) =>
      image(
        `/works/applications/oneteam/IMG_${number}.png`,
        `OneTeam mobile prototype screen ${number}`
      )
    ),
  },
  {
    slug: "video",
    number: "11",
    title: "Moving image",
    shortTitle: "Showreel",
    meta: "Direction · Production · Motion",
    period: "2014 · Updated 2022",
    categories: ["Video"],
    colour: "red",
    cover: "/works/videos/thumbnail.png",
    coverAlt: "Video showreel thumbnail",
    intro:
      "Selected video production, compositing and moving-image work.",
    body:
      "A showreel spanning teaching, directing and producing screen media, with animation and motion-graphics work added in 2022.",
    gallery: [
      video(
        "/works/videos/Showreel2014-web.mp4",
        "Clément Girault showreel 2014 — animation and motion graphics updated in 2022",
        "/works/videos/thumbnail.png"
      ),
    ],
  },
  {
    slug: "music",
    number: "12",
    title: "Music",
    shortTitle: "Composed and improvised",
    meta: "Composition · Improvisation · Sound",
    period: "Selected recordings",
    categories: ["Sound"],
    colour: "yellow",
    cover: "/works/sound/music-thumbnail/SomewhereFarAway.png",
    coverAlt: "Somewhere Far Away music artwork",
    intro:
      "Composed and improvised music spanning atmospheric, electronic and collaborative work.",
    body:
      "A small listening room from the wider archive, presented as full-length audio rather than decorative background sound.",
    tracks: [
      {
        title: "Somewhere Far Away (extract)",
        src: "/works/sound/composed music/somewhere_far_away.mp3",
      },
      {
        title: "Blue Mountain",
        src: "/works/sound/composed music/blue_mountain.m4a",
      },
      {
        title: "Gravity Pull (improvisation)",
        src: "/works/sound/improvised music/gravity_pull.mp3",
      },
      {
        title: "Rewind (improvisation)",
        src: "/works/sound/improvised music/rewind.mp3",
      },
    ],
    gallery: [
      image(
        "/works/sound/music-thumbnail/SomewhereFarAway.png",
        "Somewhere Far Away music artwork"
      ),
    ],
  },
];

export const featuredProjects = [
  projects[0],
  projects[1],
  projects[5],
  projects[4],
];

export const archiveProjects = [
  projects[3],
  projects[2],
  projects[6],
  projects[8],
];

export const disciplines = [
  "Digital strategy",
  "Product design",
  "Service design",
  "Digital delivery",
  "Web design",
  "Platform migrations",
  "Process automation",
  "Software development",
  "Chatbot design",
];
